import React, { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '../store/authStore';
import { api } from '../services/api';
import toast from 'react-hot-toast';
import { playNotificationSound } from '../utils/notificationSounds';

// Store to track shown notifications (prevent duplicates)
const shownNotifications = new Set();

const useUpcomingNotifications = () => {
  const { isAuthenticated } = useAuthStore();
  const intervalRef = useRef(null);

  // Fetch upcoming notifications
  const { data } = useQuery({
    queryKey: ['upcoming-notifications'],
    queryFn: async () => {
      const response = await api.get('/notifications/upcoming');
      return response.data.data;
    },
    enabled: isAuthenticated,
    refetchInterval: 60000, // Check every minute
    staleTime: 30000, // 30 seconds
  });

  useEffect(() => {
    if (!isAuthenticated || !data) return;

    const items = data.items || [];
    
    // Show notifications for items within 2 hours
    items.forEach(item => {
      const notificationKey = item.type + '-' + item.id;
      
      // Skip if already shown
      if (shownNotifications.has(notificationKey)) {
        return;
      }

      const itemDateTime = new Date(item.datetime);
      const now = new Date();
      const timeDiff = itemDateTime.getTime() - now.getTime();
      const hoursUntil = Math.floor(timeDiff / (1000 * 60 * 60));
      const minutesUntil = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));

      // Only show if within 2 hours
      if (timeDiff > 0 && timeDiff <= 2 * 60 * 60 * 1000) {
        // Mark as shown
        shownNotifications.add(notificationKey);

        // Format time string
        var timeString = '';
        if (hoursUntil > 0) {
          timeString = hoursUntil + ' saat ' + (minutesUntil > 0 ? minutesUntil + ' dakika' : '');
        } else {
          timeString = minutesUntil + ' dakika';
        }
        
        // Format datetime for display (Turkey timezone)
        const displayDateTime = new Date(item.datetime).toLocaleString('tr-TR', {
          timeZone: 'Europe/Istanbul',
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        });

        const toastStyle = {
          background: '#FEF3C7',
          border: '1px solid #FCD34D',
          color: '#92400E',
          borderRadius: '8px',
          padding: '12px 14px',
          maxWidth: '320px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '10px',
        };

        const closeBtnStyle = {
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          color: '#92400E',
          flexShrink: 0,
          padding: '2px',
          fontSize: '16px',
          lineHeight: 1,
          opacity: 0.7,
        };

        // Play sound for this upcoming notification type
        playNotificationSound(item.type);

        // Show toast notification
        if (item.type === 'planned_case') {
          toast.custom(
            (t) => (
              <div style={toastStyle}>
                <span style={{ fontSize: '18px', flexShrink: 0, marginTop: '2px' }}>⏰</span>
                <div style={{ flex: 1, fontSize: '13px', lineHeight: '1.5' }}>
                  <div style={{ fontWeight: '700', fontSize: '14px', marginBottom: '4px' }}>
                    Planlı Vaka Yaklaşıyor
                  </div>
                  <div>Planlı Vaka: {item.title}</div>
                  <div>Vaka No: {item.case_number}</div>
                  <div style={{ marginTop: '4px', color: '#B45309', fontWeight: '600' }}>
                    {displayDateTime} — {timeString} sonra
                  </div>
                </div>
                <button onClick={() => toast.dismiss(t.id)} style={closeBtnStyle} title="Kapat">
                  ✕
                </button>
              </div>
            ),
            { duration: 8000 }
          );
        } else if (item.type === 'organization') {
          toast.custom(
            (t) => (
              <div style={toastStyle}>
                <span style={{ fontSize: '18px', flexShrink: 0, marginTop: '2px' }}>⏰</span>
                <div style={{ flex: 1, fontSize: '13px', lineHeight: '1.5' }}>
                  <div style={{ fontWeight: '700', fontSize: '14px', marginBottom: '4px' }}>
                    Organizasyon Yaklaşıyor
                  </div>
                  <div>{item.title}</div>
                  <div>Organizasyon No: {item.organization_number}</div>
                  <div style={{ marginTop: '4px', color: '#B45309', fontWeight: '600' }}>
                    {displayDateTime} — {timeString} sonra
                  </div>
                </div>
                <button onClick={() => toast.dismiss(t.id)} style={closeBtnStyle} title="Kapat">
                  ✕
                </button>
              </div>
            ),
            { duration: 8000 }
          );
        }

        // Remove from shown set after 2 hours (cleanup)
        setTimeout(function() {
          shownNotifications.delete(notificationKey);
        }, 2 * 60 * 60 * 1000);
      }
    });

  }, [data, isAuthenticated]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  return null;
};

export default useUpcomingNotifications;
